import { type JSX, useId } from 'react';

interface DataPoint {
  label: string;
  value: number;
}

/**
 * Cardinal spline interpolation — produces smooth cubic-bezier curves
 * through all data points. Tension 0 = straight lines, 1 = very curvy.
 */
function toSplinePath(points: Array<{ x: number; y: number }>, tension = 0.35): string {
  if (points.length < 2) return '';
  if (points.length === 2) return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;

  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

const VB_W = 400;
const VB_H = 140;
const PAD = { b: 26, l: 16, r: 16, t: 18 };
const CHART_W = VB_W - PAD.l - PAD.r;
const CHART_H = VB_H - PAD.t - PAD.b;

export function ActivityChart({
  className = 'mb-6',
  data,
  emptyLabel = 'Not enough data to chart yet',
  title,
}: {
  className?: string;
  data: DataPoint[];
  emptyLabel?: string;
  title: string;
}): JSX.Element | null {
  const gradientId = useId();

  if (data.length < 2) {
    return (
      <div className={className}>
        <h2 className="text-sm font-semibold text-foreground mb-3">{title}</h2>
        <div className="rounded-lg border border-white/5 bg-surface p-5 text-center text-xs text-muted py-10">
          {emptyLabel}
        </div>
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => d.value), 1);

  const pts = data.map((d, i) => ({
    x: PAD.l + (i / (data.length - 1)) * CHART_W,
    y: PAD.t + CHART_H - (d.value / maxVal) * CHART_H * 0.88,
  }));

  const linePath = toSplinePath(pts);
  const areaPath = `${linePath} L${pts[pts.length - 1].x},${PAD.t + CHART_H} L${pts[0].x},${PAD.t + CHART_H} Z`;

  const labelStep = Math.max(1, Math.ceil(data.length / 10));

  return (
    <div className={className}>
      <h2 className="text-sm font-semibold text-foreground mb-3">{title}</h2>
      <div className="rounded-lg border border-white/5 bg-surface p-4">
        <div className="text-primary">
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            className="w-full"
            style={{ height: '140px' }}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.01" />
              </linearGradient>
            </defs>

            {/* Subtle horizontal grid lines */}
            {[0.25, 0.5, 0.75].map(pct => (
              <line
                key={pct}
                x1={PAD.l}
                y1={PAD.t + CHART_H * (1 - pct)}
                x2={PAD.l + CHART_W}
                y2={PAD.t + CHART_H * (1 - pct)}
                stroke="white"
                strokeOpacity="0.04"
              />
            ))}

            {/* Gradient area fill */}
            <path d={areaPath} fill={`url(#${gradientId})`} />

            {/* Smooth data line */}
            <path
              d={linePath}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Data point dots + value labels */}
            {pts.map((p, i) => (
              <g key={data[i].label}>
                {/* Glow ring */}
                <circle cx={p.x} cy={p.y} r="5" fill="currentColor" opacity="0.1" />
                {/* Dot */}
                <circle cx={p.x} cy={p.y} r="3" fill="currentColor" opacity="0.85" />
                {/* Value above dot (hide zeros for cleanliness) */}
                {data[i].value > 0 && (
                  <text
                    x={p.x}
                    y={p.y - 10}
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize="9"
                    fontWeight="600"
                    opacity="0.55"
                  >
                    {data[i].value}
                  </text>
                )}
              </g>
            ))}

            {/* X-axis labels */}
            {data.map((d, i) =>
              i % labelStep === 0 || i === data.length - 1 ? (
                <text
                  key={d.label}
                  x={pts[i].x}
                  y={VB_H - 4}
                  textAnchor="middle"
                  fill="white"
                  fillOpacity="0.35"
                  fontSize="9"
                >
                  {d.label}
                </text>
              ) : null,
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}
